class CollectionTabsRWD extends HTMLElement {
  constructor() {
    super();

    /** @type {HTMLElement | null} */
    this.tablist = this.querySelector('[role="tablist"]');

    /** @type {HTMLElement[]} */
    this.tabs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);

    /** @type {HTMLElement[]} */
    this.panels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tabpanel"]')]);

    /** Animation duration in ms — must match CSS transition on .__panel. */
    this.fadeDuration = 260;

    /** @type {boolean} */
    this.isSwitching = false;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.switchTab(tab));
    });

    if (this.tablist) {
      this.tablist.addEventListener('keydown', (e) => this.handleKeydown(e));
    }

    // Position the sliding indicator under the initially-selected tab.
    // Wait for fonts/layout, then enable the transition via .is-ready.
    requestAnimationFrame(() => {
      this.updateIndicator();
      if (this.tablist) this.tablist.classList.add('is-ready');
    });

    // Reposition on resize (font/layout changes).
    this._onResize = () => this.updateIndicator();
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  /**
   * Move the sliding indicator under the currently selected tab.
   */
  updateIndicator() {
    if (!this.tablist) return;
    const active = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true');
    if (!active) return;
    this.tablist.style.setProperty('--tab-indicator-x', active.offsetLeft + 'px');
    this.tablist.style.setProperty('--tab-indicator-w', active.offsetWidth + 'px');
  }

  /** @param {HTMLElement} selectedTab */
  switchTab(selectedTab) {
    if (this.isSwitching) return;
    if (selectedTab.getAttribute('aria-selected') === 'true') return;

    // Scroll only the tabs wrapper horizontally — never touch window scroll
    this.scrollTabIntoView(selectedTab);

    this.tabs.forEach((tab) => {
      const isSelected = tab === selectedTab;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
    });

    // Slide the indicator to the newly selected tab.
    this.updateIndicator();

    const targetId = selectedTab.getAttribute('aria-controls');
    const nextPanel = this.panels.find((p) => p.id === targetId);
    const currentPanel = this.panels.find((p) => !p.hidden);

    if (!nextPanel || nextPanel === currentPanel) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      this.panels.forEach((p) => {
        p.hidden = p !== nextPanel;
      });
      return;
    }

    this.isSwitching = true;

    // Lock tabs wrapper height to avoid flex-wrap reflow mid-transition
    const tabsWrapper = /** @type {HTMLElement | null} */ (
      this.querySelector('.collection-tabs-rwd__tabs-wrapper')
    );
    const tabsBar = /** @type {HTMLElement | null} */ (this.querySelector('.collection-tabs-rwd__tabs'));
    if (tabsWrapper) {
      tabsWrapper.style.minHeight = tabsWrapper.offsetHeight + 'px';
    }
    if (tabsBar) {
      tabsBar.style.minHeight = tabsBar.offsetHeight + 'px';
    }

    const fadeOut = () =>
      /** @type {Promise<void>} */ (
        new Promise((resolve) => {
          if (!currentPanel) {
            resolve();
            return;
          }
          currentPanel.classList.add('collection-tabs-rwd__panel--leaving');
          setTimeout(resolve, this.fadeDuration);
        })
      );

    fadeOut().then(() => {
      if (currentPanel) {
        currentPanel.classList.remove('collection-tabs-rwd__panel--leaving');
        currentPanel.hidden = true;
      }

      // Prepare next panel invisible, then reveal & fade in
      nextPanel.classList.add('collection-tabs-rwd__panel--entering');
      nextPanel.hidden = false;

      // Force reflow so the transition takes effect
      // eslint-disable-next-line no-unused-expressions
      nextPanel.offsetHeight;

      requestAnimationFrame(() => {
        nextPanel.classList.remove('collection-tabs-rwd__panel--entering');
      });

      setTimeout(() => {
        if (tabsWrapper) tabsWrapper.style.minHeight = '';
        if (tabsBar) tabsBar.style.minHeight = '';
        this.isSwitching = false;
      }, this.fadeDuration);
    });
  }

  /**
   * Scroll the tabs wrapper horizontally so the selected tab is visible,
   * without ever scrolling the window.
   * @param {HTMLElement} selectedTab
   */
  scrollTabIntoView(selectedTab) {
    const wrapper = /** @type {HTMLElement | null} */ (
      this.querySelector('.collection-tabs-rwd__tabs-wrapper')
    );
    if (!wrapper) return;
    if (wrapper.scrollWidth <= wrapper.clientWidth) return;

    const tabRect = selectedTab.getBoundingClientRect();
    const wrapRect = wrapper.getBoundingClientRect();

    // Only scroll when tab is partially out of view
    if (tabRect.left >= wrapRect.left && tabRect.right <= wrapRect.right) return;

    const target =
      selectedTab.offsetLeft -
      (wrapper.clientWidth - selectedTab.offsetWidth) / 2;

    const max = wrapper.scrollWidth - wrapper.clientWidth;
    const left = Math.max(0, Math.min(target, max));

    wrapper.scrollTo({ left, behavior: 'smooth' });
  }

  /** @param {KeyboardEvent} e */
  handleKeydown(e) {
    const currentIndex = this.tabs.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (currentIndex === -1) return;

    let newIndex;
    switch (e.key) {
      case 'ArrowRight':
        newIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
        newIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    e.preventDefault();
    const nextTab = this.tabs[newIndex];
    if (nextTab) {
      this.switchTab(nextTab);
      nextTab.focus();
    }
  }
}

customElements.define('collection-tabs-rwd', CollectionTabsRWD);
